import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { RootLayoutComponent } from './shared/layout/root-layout/root-layout.component';
import { BaseGuard } from './shared/guards/base.guard';
import { AuthGuard } from './shared/guards/auth.guard';
import { OptOutComponent } from './opt-out/opt-out.component';
import { LegacyRedirectComponent } from './shared/components/legacy-redirect/legacy-redirect.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { PayInvoiceComponent } from './pay-invoice/pay-invoice.component';

const routes: Routes = [
  {
    path: 'signup-share',
    loadChildren: () => import('./shares/shares.module').then((m) => m.SharesModule),
  },
  {
    // Single-page paid signup checkout (COM-8507). Public, isolated, top-level route — kept a
    // sibling of `signup-share` so the greedy `''` AuthModule chunk below doesn't swallow it.
    path: 'checkout',
    title: 'Checkout | Sync.com',
    loadChildren: () => import('./checkout/checkout.module').then((m) => m.CheckoutModule),
  },
  {
    path: '',
    component: RootLayoutComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [BaseGuard],
        children: [],
      },
      {
        path: 'users',
        loadChildren: () => import('./users-roles/users-roles.module').then((m) => m.UsersRolesModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'settings',
        title: 'Settings | Sync.com',
        loadChildren: () => import('./settings/settings.module').then((m) => m.SettingsModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'files',
        title: 'Files | Sync.com',
        loadChildren: () => import('./file-list/file-list.module').then((m) => m.FileListModule),
        data: { defaultSyncIdKey: 'syncus_sync_id' },
        canActivate: [AuthGuard],
      },
      {
        path: 'files/:sync_id',
        data: { defaultSyncIdKey: 'syncus_sync_id' },
        loadChildren: () => import('./file-list/file-list.module').then((m) => m.FileListModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'file',
        loadChildren: () => import('./file/file.module').then((m) => m.FileModule),
      },
      {
        path: 'recents',
        title: 'Recents | Sync.com',
        loadChildren: () => import('./recents/recents.module').then((m) => m.RecentsModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'vault',
        title: 'Vault | Sync.com',
        loadChildren: () => import('./file-list/file-list.module').then((m) => m.FileListModule),
        data: { defaultSyncIdKey: 'web_sync_id' },
        canActivate: [AuthGuard],
      },
      {
        path: 'vault/:sync_id',
        data: { defaultSyncIdKey: 'web_sync_id' },
        loadChildren: () => import('./file-list/file-list.module').then((m) => m.FileListModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'shares',
        title: 'Shares | Sync.com',
        loadChildren: () => import('./shares/shares.module').then((m) => m.SharesModule),
      },
      {
        path: 'starred',
        title: 'Starred | Sync.com',
        loadChildren: () => import('./starred/starred.module').then((m) => m.StarredModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'starred/:sync_id',
        loadChildren: () => import('./file-list/file-list.module').then((m) => m.FileListModule),
        canActivate: [AuthGuard],
      },
      {
        path: 'file-history',
        title: 'Version History | Sync.com',
        loadChildren: () => import('./file/file.module').then((m) => m.FileModule),
      },
      {
        path: 'files/file-history',
        title: 'Version History | Sync.com',
        loadChildren: () => import('./file/file.module').then((m) => m.FileModule),
      },
      {
        path: 'vault/file-history',
        title: 'Version History | Sync.com',
        loadChildren: () => import('./file/file.module').then((m) => m.FileModule),
      },
      {
        path: 'support-form',
        loadChildren: () => import('./file-list/file-list.module').then((m) => m.FileListModule),
        data: { defaultSyncIdKey: 'syncus_sync_id', openSupportForm: true },
        canActivate: [AuthGuard],
      },
      {
        path: 'opt-out',
        component: OptOutComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'pay-invoices',
        component: PayInvoiceComponent,
        canActivate: [AuthGuard],
      },
      // legacy routes
      { path: 'events', redirectTo: 'settings/events' },
      { path: 'links', redirectTo: 'shares/links' },
      { path: 'multi-user', redirectTo: 'users', pathMatch: 'full' },
      { path: 'multi-user/users', redirectTo: 'users', pathMatch: 'full' },
      { path: 'multi-user/roles', redirectTo: 'users/roles', pathMatch: 'full' },
      { path: 'account', redirectTo: 'settings', pathMatch: 'full' },
      { path: 'account/info', redirectTo: 'settings/profile' },
      { path: 'account/renew', redirectTo: 'pay-invoices' },
      { path: 'account/billing', redirectTo: 'settings/billing', pathMatch: 'full' },
      { path: 'account/billing/profile', redirectTo: 'settings/billing' },
      { path: 'account/devices', redirectTo: 'settings/devices' },
      { path: 'account/security', redirectTo: 'settings/security' },
      { path: 'account/billing/invoice/:id', redirectTo: '/settings/billing' },
      { path: 'account/upgrade', component: LegacyRedirectComponent },
      { path: 'account/getspace', component: LegacyRedirectComponent },
      { path: 'getspace', component: LegacyRedirectComponent },
      { path: 'upgrade', component: LegacyRedirectComponent },
      { path: 'account/getstarted', component: LegacyRedirectComponent },
    ],
  },
  {
    path: 'dl',
    loadChildren: () => import('./link-consume/link-consume.module').then((m) => m.LinkConsumeModule),
  },
  {
    path: 'dl/:cachekey',
    loadChildren: () => import('./link-consume/link-consume.module').then((m) => m.LinkConsumeModule),
  },
  {
    path: 'dl/:cachekey/view/:type/:id',
    loadChildren: () => import('./link-consume/link-consume.module').then((m) => m.LinkConsumeModule),
  },
  {
    path: 'comment-iframe',
    loadChildren: () => import('./link-consume/link-consume.module').then((m) => m.LinkConsumeModule),
  },
  {
    path: '',
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule),
  },
  {
    // <-- Always last
    path: '**',
    component: NotFoundComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
